import { useContext, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  findCommonTimeRangeAcrossWeek,
  getDailyRanges,
  getDisabledWeekHoursEvents,
  mergeInactiveEvents,
  mergeTimeRangesAcrossMidnight,
  removeEventsByDisabledHours,
} from "common/Calendars/utils.ts";
import useEventTimePicker from "common/Calendars/useEventTimePicker.ts";
import { OperationHoursContext } from "features/bots/BotForm/OperationHours/OperationHours.context.ts";

const useDisableWeekHours = () => {
  const { myEvents, setMyEvents } = useContext(OperationHoursContext)!;
  const { props: timePickerProps } = useEventTimePicker();

  const [timeRange, setTimeRange] = useState<[Dayjs | null, Dayjs | null]>();
  const [disabledHours, setDisabledHours] = useState<DisabledHours[]>([]);
  const [showNewRow, setShowNewRow] = useState(false);

  useEffect(() => {
    if (myEvents) {
      const dailyRanges = getDailyRanges(myEvents);
      const commonRanges = findCommonTimeRangeAcrossWeek(dailyRanges);
      const mergedCommonRanges = mergeTimeRangesAcrossMidnight(commonRanges);

      const disabled: DisabledHours[] = mergedCommonRanges.map((range) => ({
        value: [dayjs(range.start), dayjs(range.end)],
        isEdit: false,
      }));
      setDisabledHours(disabled);
    }
  }, [myEvents]);

  const setWeekHours = (editIndex?: number) => {
    if (!timeRange) return;

    setDisabledHours((prev) => {
      let updated: DisabledHours[] = [...prev];
      if (editIndex !== undefined) {
        const oldValue = prev[editIndex].value;
        updated = prev.map((item, index) =>
          index === editIndex ? { isEdit: false, value: timeRange } : item
        );
        setMyEvents((prev) => {
          const eventsWithoutOld = removeEventsByDisabledHours(
            oldValue,
            prev || []
          );
          const updatedEvents = [
            ...eventsWithoutOld,
            ...getDisabledWeekHoursEvents(timeRange),
          ];
          return mergeInactiveEvents(updatedEvents);
        });
      } else {
        updated.push({ value: timeRange, isEdit: false });
        setMyEvents((prev) => {
          const updated = [
            ...(prev || []),
            ...getDisabledWeekHoursEvents(timeRange),
          ];
          return mergeInactiveEvents(updated);
        });
      }
      return updated;
    });
    if (showNewRow) setShowNewRow(false);
  };

  const onRemoveClick = (indexToRemove: number) => {
    setDisabledHours((prev) => {
      const removed = prev[indexToRemove];
      const updated = [...prev];
      updated.splice(indexToRemove, 1);

      setMyEvents((prevEvents) =>
        removeEventsByDisabledHours(removed.value, prevEvents || [])
      );
      return updated;
    });
  };

  const onEditClick = (editIndex: number) => {
    setDisabledHours((prev) => {
      return prev.map((item, index) =>
        index === editIndex ? { ...item, isEdit: true } : item
      );
    });
  };

  return {
    disabledHours,
    setWeekHours,
    onRemoveClick,
    onEditClick,
    showNewRow,
    setShowNewRow,
    timePickerProps,
    setTimeRange,
  };
};

export default useDisableWeekHours;

type DisabledHours = {
  value: [Dayjs | null, Dayjs | null];
  isEdit?: boolean;
};
